import React from "react";
import CompareMap from "./CompareMap";
import "./CompareStationPopup.css";

const METRIC_LABELS = {
  availability: "Availability %",
  timeliness:   "Timeliness (min)",
  quality:      "O-B Departure",
};

const fmtVal = (metric, obj) => {
  if (!obj) return "—";
  if (metric === "availability" && obj.pct        != null) return `${obj.pct.toFixed(1)}%`;
  if (metric === "timeliness"   && obj.minutes    != null) return `${Math.round(obj.minutes)} min`;
  if (metric === "quality"      && obj.abs_value  != null) return `${obj.abs_value.toFixed(2)}`;
  return "—";
};

const fmtDelta = (metric, p1, p2) => {
  if (!p1 || !p2) return null;
  if (metric === "availability") {
    const d = (p2.pct || 0) - (p1.pct || 0);
    return { v: `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`, up: d >= 0 };
  }
  if (metric === "timeliness") {
    const d = Math.round((p2.minutes || 0) - (p1.minutes || 0));
    return { v: `${d >= 0 ? "+" : ""}${d} min`, up: d <= 0 };
  }
  if (metric === "quality") {
    const d = (p2.abs_value || 0) - (p1.abs_value || 0);
    return { v: `${d >= 0 ? "+" : ""}${d.toFixed(2)}`, up: d <= 0 };
  }
  return null;
};

const CatPill = ({ cat }) => {
  if (!cat || !cat.label) {
    return <span className="popup-cat-pill popup-cat-pill--empty">—</span>;
  }
  return (
    <span className="popup-cat-pill" style={{ background: cat.color || "#9ca3af" }}>
      {cat.label}
    </span>
  );
};

const StepsBadge = ({ steps }) => {
  if (steps == null) return null;
  const n = parseInt(steps);
  if (n === 0) return <span style={{ color: "#6b7280", fontSize: 10 }}>0 steps</span>;
  const isDown = n < 0;
  return (
    <span
      className={`csp-steps-badge ${isDown ? "csp-steps-badge--down" : "csp-steps-badge--up"}`}
      style={{ fontSize: 10 }}
    >
      {isDown ? `↓ ${n}` : `↑ +${n}`} steps
    </span>
  );
};

const MetricBlock = ({ valLabel, metric, p1, p2 }) => {
  const delta = fmtDelta(metric, p1, p2);
  const steps = (p1 && p2 && p2.rank != null && p1.rank != null)
    ? p1.rank - p2.rank : null;  // positive = improvement (rank fell = better)
  return (
    <>
      <tr>
        <td className="popup-td-label">{valLabel}</td>
        <td className="popup-td-val">{fmtVal(metric, p1)}</td>
        <td className="popup-td-val">{fmtVal(metric, p2)}</td>
        <td className={`popup-td-change ${delta
          ? (delta.up ? "popup-td-change--up" : "popup-td-change--down")
          : "popup-td-change--none"}`}>
          {delta ? delta.v : "—"}
        </td>
      </tr>
      <tr className="popup-cat-row">
        <td className="popup-td-label" style={{ paddingLeft: 18 }}>Category</td>
        <td><CatPill cat={p1} /></td>
        <td><CatPill cat={p2} /></td>
        <td><StepsBadge steps={steps} /></td>
      </tr>
    </>
  );
};

const CONF_WIDTH = { High: "90%", Medium: "55%", Low: "25%" };
const CONF_COLOR = { High: "#16a34a", Medium: "#d97706", Low: "#dc2626" };

const Sparkline = ({ data, metric, date1, date2 }) => {
  const [hover, setHover] = React.useState(null);

  if (!data || data.length < 2) {
    return <div className="popup-sparkline-empty">Not enough data for trend</div>;
  }
  const W = 260;
  const H = 90;
  const PAD_L = 32;
  const PAD_R = 8;
  const PAD_T = 6;
  const PAD_B = 14;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const vals = data.map(d => d.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const range = maxV - minV || 1;
  const xStep = plotW / (data.length - 1);

  const xy = (d, i) => {
    const x = PAD_L + i * xStep;
    const y = PAD_T + plotH - ((d.value - minV) / range) * plotH;
    return { x, y };
  };

  const points = data.map((d, i) => {
    const { x, y } = xy(d, i);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const pathD = `M ${points.join(" L ")}`;
  const areaD = `${pathD} L ${(PAD_L + (data.length - 1) * xStep).toFixed(1)},${(PAD_T + plotH).toFixed(1)} L ${PAD_L},${(PAD_T + plotH).toFixed(1)} Z`;

  const lineColor = metric === "availability" ? "#2563eb" : metric === "timeliness" ? "#d97706" : "#7c3aed";

  // Y-axis: 3 ticks
  const yTicks = [maxV, (maxV + minV) / 2, minV];
  const fmtY = (v) => {
    if (metric === "availability") return `${v.toFixed(0)}%`;
    if (metric === "timeliness") return `${v.toFixed(0)}`;
    return v.toFixed(2);
  };

  // Find x positions for period markers
  const findXForDate = (targetDate) => {
    if (!targetDate) return null;
    const target = targetDate.substring(0, 10);
    const idx = data.findIndex(d => d.date === target || d.date.substring(0, 10) === target);
    if (idx >= 0) return PAD_L + idx * xStep;
    // Find closest
    let bestIdx = 0;
    let bestDiff = Infinity;
    data.forEach((d, i) => {
      const dDiff = Math.abs(new Date(d.date.substring(0, 10)) - new Date(target));
      if (dDiff < bestDiff) { bestDiff = dDiff; bestIdx = i; }
    });
    return PAD_L + bestIdx * xStep;
  };

  const xP1 = findXForDate(date1);
  const xP2 = findXForDate(date2);

  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    // Find nearest data point
    let bestIdx = 0;
    let bestDist = Infinity;
    data.forEach((d, i) => {
      const { x } = xy(d, i);
      const dist = Math.abs(x - px);
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    });
    setHover(bestIdx);
  };

  const hoverPt = hover != null ? xy(data[hover], hover) : null;

  return (
    <svg
      className="popup-sparkline"
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHover(null)}
    >
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity={0.2} />
          <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
        </linearGradient>
      </defs>

      {/* Y-axis grid lines + labels */}
      {yTicks.map((v, i) => {
        const y = PAD_T + (i / 2) * plotH;
        return (
          <g key={i}>
            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#f3f4f6" strokeWidth={1} />
            <text x={PAD_L - 4} y={y + 3} textAnchor="end" fontSize={8} fill="#9ca3af">
              {fmtY(v)}
            </text>
          </g>
        );
      })}

      {/* Period A vertical line */ }
      {xP1 != null && (
        <g>
          <line x1={xP1} y1={PAD_T} x2={xP1} y2={PAD_T + plotH} stroke="#dc2626" strokeWidth={1} strokeDasharray="3,2" />
          <text x={xP1} y={PAD_T - 1} textAnchor="middle" fontSize={7} fill="#dc2626" fontWeight="700">P1</text>
        </g>
      )}

      {/* Period B vertical line */ }
      {xP2 != null && (
        <g>
          <line x1={xP2} y1={PAD_T} x2={xP2} y2={PAD_T + plotH} stroke="#16a34a" strokeWidth={1} strokeDasharray="3,2" />
          <text x={xP2} y={PAD_T - 1} textAnchor="middle" fontSize={7} fill="#16a34a" fontWeight="700">P2</text>
        </g>
      )}

      {/* Area + line */}
      <path d={areaD} fill="url(#sparkGrad)" />
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth={1.5} />

      {/* Hover point + tooltip */}
      {hoverPt && hover != null && (
        <g>
          <line
            x1={hoverPt.x} y1={PAD_T} x2={hoverPt.x} y2={PAD_T + plotH}
            stroke="#d1d5db" strokeWidth={0.5} strokeDasharray="2,2"
          />
          <circle cx={hoverPt.x} cy={hoverPt.y} r={3} fill={lineColor} stroke="#fff" strokeWidth={1} />
          <g transform={`translate(${Math.min(hoverPt.x + 6, W - 70)},${Math.max(hoverPt.y - 20, PAD_T)})`}>
            <rect width={64} height={22} rx={3} fill="#1f2937" opacity={0.92} />
            <text x={4} y={9} fontSize={8} fill="#fff" fontWeight="600">
              {data[hover].date.substring(5, 10)}
            </text>
            <text x={4} y={18} fontSize={8} fill="#fff">
              {fmtY(data[hover].value)}
            </text>
          </g>
        </g>
      )}
    </svg>
  );
};

const CompareStationPopup = ({ mapProps, detail, meta }) => {
  const props  = mapProps || {};
  const name   = props.name    || detail?.station?.name    || "Unknown station";
  const wid    = props.wigosid || detail?.station?.wigosid || "";
  const country= props.country || detail?.station?.country || "";

  const handleClose = () => {
    if (CompareMap.closePopup) CompareMap.closePopup();
  };

  if (!detail) {
    return (
      <div className="popup-root">
        <div className="popup-header">
          <div className="popup-header-left">
            <div className="popup-station-name">{name}</div>
            <div className="popup-wigosid">{wid}</div>
            <div className="popup-country">{country}</div>
          </div>
          <button className="popup-close" data-action="close" title="Close">✕</button>
        </div>
        <div className="cmap-popup-loading">Loading details…</div>
      </div>
    );
  }

  const { metrics, cause, date1, date2, driver_analysis, center_evidence, timeseries } = detail;
  const metric = (meta && meta.metric) || detail?.meta?.metric || "availability";
  const avail  = metrics && metrics.availability;
  const timel  = metrics && metrics.timeliness;
  const qual   = metrics && metrics.quality;

  const causeLabel = cause && cause.cause
    ? cause.cause.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null;
  const confidence = cause && cause.confidence ? cause.confidence : null;
  const confPct    = CONF_WIDTH[confidence] || "0%";
  const confColor  = CONF_COLOR[confidence] || "#9ca3af";

  // Once detail is loaded use it as the authoritative source for no-data flags.
  // This overrides potentially stale GeoJSON props from before the last Apply.
  const hasP1Data = (avail && avail.p1) || (timel && timel.p1) || (qual && qual.p1);
  const hasP2Data = (avail && avail.p2) || (timel && timel.p2) || (qual && qual.p2);
  const showNoDataP1 = !hasP1Data;
  const showNoDataP2 = !hasP2Data;

  return (
    <div className="popup-root">
      <div className="popup-header">
        <div className="popup-header-left">
          <div className="popup-station-name">{name}</div>
          <div className="popup-wigosid">{wid}</div>
          <div className="popup-country">{country}</div>
        </div>
        <button className="popup-close" data-action="close" title="Close">✕</button>
      </div>

      <div className="popup-meta">
        <strong>Period 1:</strong> {date1 || (meta && meta.date1) || "—"}
        &nbsp;→&nbsp;
        <strong>Period 2:</strong> {date2 || (meta && meta.date2) || "—"}
        {showNoDataP1 && (
          <span style={{ marginLeft: 8, background: "#7b2d8b", color: "#fff",
            fontSize: 9, padding: "1px 5px", borderRadius: 8 }}>
            New in P2
          </span>
        )}
        {showNoDataP2 && (
          <span style={{ marginLeft: 8, background: "#000", color: "#fff",
            fontSize: 9, padding: "1px 5px", borderRadius: 8 }}>
            Stopped P2
          </span>
        )}
      </div>

      <div className="popup-body">
        {/* ── Left column: metrics + cause ─────────────────────────── */}
        <div className="popup-col popup-col--left">
          <table className="popup-table">
            <colgroup>
              <col /><col /><col /><col />
            </colgroup>
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Period 1</th>
                <th>Period 2</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {avail && (
                <MetricBlock
                  valLabel="Availability %"
                  metric="availability"
                  p1={avail.p1}
                  p2={avail.p2}
                />
              )}
              {timel && (
                <MetricBlock
                  valLabel="Timeliness (median)"
                  metric="timeliness"
                  p1={timel.p1}
                  p2={timel.p2}
                />
              )}
              {qual && (
                <MetricBlock
                  valLabel={`O-B Departure (${meta && meta.varId === "110" ? "hPa" : "avg"})`}
                  metric="quality"
                  p1={qual.p1}
                  p2={qual.p2}
                />
              )}
            </tbody>
          </table>

          {causeLabel && (
            <div className="popup-cause">
              <div className="popup-cause__label">Likely Cause</div>
              <div className="popup-cause__value">{causeLabel}</div>
              {confidence && (
                <>
                  <div className="popup-cause__conf-label">
                    Confidence (overall): {confidence}
                  </div>
                  <div className="popup-cause__bar-track">
                    <div
                      className="popup-cause__bar-fill"
                      style={{ width: confPct, background: confColor }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Right column: sparkline + driver + evidence ──────────── */}
        <div className="popup-col popup-col--right">
          {/* ── 30-day trend sparkline ──────────────────────────────── */}
          {timeseries && timeseries.length > 0 && (
            <div className="popup-section">
              <div className="popup-section__title">
                30-day trend — {METRIC_LABELS[metric] || metric}
              </div>
              <Sparkline data={timeseries} metric={metric} date1={date1 || (meta && meta.date1)} date2={date2 || (meta && meta.date2)} />
            </div>
          )}

          {/* ── Why did category change? ────────────────────────────── */}
          {driver_analysis && driver_analysis.primary_driver && (
            <div className="popup-section popup-driver">
              <div className="popup-section__title">Why did category change?</div>
              <div className="popup-driver__primary">
                <span className="popup-driver__label">Primary driver:</span>{" "}
                {driver_analysis.primary_driver}
              </div>
              {driver_analysis.details && driver_analysis.details.length > 0 && (
                <ul className="popup-driver__details">
                  {driver_analysis.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ── Monitoring Centre Evidence ──────────────────────────── */}
          {center_evidence && center_evidence.length > 0 && (
            <div className="popup-section popup-evidence">
              <div className="popup-section__title">Monitoring Centre Evidence</div>
              <table className="popup-evidence-table">
                <colgroup>
                  <col style={{width: "16%"}} />
                  <col style={{width: "17%"}} />
                  <col style={{width: "17%"}} />
                  <col style={{width: "20%"}} />
                  <col style={{width: "30%"}} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Center</th>
                    <th>Period A</th>
                    <th>Period B</th>
                    <th>Change</th>
                    <th>Agreement</th>
                  </tr>
                </thead>
                <tbody>
                  {center_evidence.map((row, i) => (
                    <tr key={i}>
                      <td className="popup-evidence__center">{row.center}</td>
                      <td>{row.p1_value}</td>
                      <td>{row.p2_value}</td>
                      <td className={row.delta.startsWith("-") ? "popup-td-change--down" : "popup-td-change--up"}>
                        {row.delta}
                      </td>
                      <td className={
                        row.agreement === "Confirms" ? "popup-evidence__agree"
                        : row.agreement === "Does not confirm" ? "popup-evidence__disagree"
                        : ""
                      }>
                        {row.agreement}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Available Actions (full width) ──────────────────────────── */}
      <div className="popup-section popup-actions">
        <div className="popup-section__title">Available Actions</div>
        <div className="popup-actions__row">
          <button className="popup-action-btn" data-action="timeseries" title="Open full timeseries chart">
            📈 Open full timeseries
          </button>
          <button className="popup-action-btn" data-action="investigate" title="Add this station to your investigation list">
            🔍 Add to investigation
          </button>
          <button className="popup-action-btn" data-action="jira" title="Create a Jira ticket for this station">
            🎫 Open Jira ticket
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompareStationPopup;
